import { z } from 'zod';
import { formatBRL, formatIsoDate } from '../format';
import { IsoDateTime, Uuid } from './common';
import { CreateCustomerBody } from './customers';
import { StoredFile } from './files';
import { PricingCalculateRequest } from './pricing';

export const QuoteStatus = z.enum([
  'draft',
  'sent',
  'accepted',
  'declined',
  'expired',
  'converted',
]);
export type QuoteStatus = z.infer<typeof QuoteStatus>;
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: 'Rascunho',
  sent: 'Enviado',
  accepted: 'Aceito',
  declined: 'Recusado',
  expired: 'Expirado',
  converted: 'Convertido',
};
export const DeclineReason = z.enum(['price', 'lead_time', 'gave_up', 'other']);
export type DeclineReason = z.infer<typeof DeclineReason>;
export const DECLINE_REASON_LABELS: Record<DeclineReason, string> = {
  price: 'Preço',
  lead_time: 'Prazo',
  gave_up: 'Desistiu',
  other: 'Outro',
};

const ItemInput = z
  .object({
    description: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .optional()
      .describe('What the buyer gets, e.g. "Vaso Ondas M". Defaults to the product and variant.'),
    request: PricingCalculateRequest.optional().describe(
      'The Quick Quote calculation (same body as `POST /pricing/calculate`). Built from the variant’s ' +
        'print profile when left out.',
    ),
    variantId: Uuid.nullable()
      .default(null)
      .describe(
        'The catalog variant this item is (from "Começar de um produto" or "Adicionar item").',
      ),
    quantity: z
      .number()
      .int()
      .min(1)
      .max(100_000)
      .optional()
      .describe('With `variantId` and no `request` (default 1).'),
    unitPriceCentavos: z
      .number()
      .int()
      .min(0)
      .max(100_000_000)
      .nullable()
      .default(null)
      .describe('"Seu preço"; null = the rounded suggestion.'),
    belowMinimumReason: z
      .string()
      .trim()
      .min(3)
      .max(500)
      .nullable()
      .default(null)
      .describe('Required when the price is below the minimum margin.'),
    slicedFileId: Uuid.nullable().default(null),
    thumbnailFileId: Uuid.nullable().default(null),
  })
  .refine((i) => i.request !== undefined || i.variantId !== null, {
    message: 'Send the calculation (`request`) or a catalog `variantId`',
    path: ['request'],
  })
  .refine((i) => i.description !== undefined || i.variantId !== null, {
    message: 'Describe the item',
    path: ['description'],
  });
export type QuoteItemInput = z.input<typeof ItemInput>;

export const CreateQuoteBody = z
  .object({
    customerId: Uuid.nullable().default(null),
    newCustomer: CreateCustomerBody.optional().describe(
      'Creates the customer and uses it (quick create).',
    ),
    channelId: Uuid.describe('Channel the buyer will buy through; prices are evaluated for it.'),
    validUntil: z.iso
      .date()
      .optional()
      .describe('Defaults to today + the quote validity rule (7 days).'),
    notes: z.string().trim().max(2000).nullable().default(null),
    item: ItemInput,
  })
  .meta({ id: 'CreateQuoteBody' });
export type CreateQuoteBody = z.input<typeof CreateQuoteBody>;

export const AddQuoteItemBody = ItemInput;
export const UpdateQuoteItemBody = z.object({
  description: z.string().trim().min(1).max(200).optional(),
  quantity: z
    .number()
    .int()
    .min(1)
    .max(100_000)
    .optional()
    .describe('Recomputed with the frozen costs.'),
  unitPriceCentavos: z.number().int().min(0).max(100_000_000).nullable().optional(),
  belowMinimumReason: z.string().trim().min(3).max(500).nullable().optional(),
});
export const UpdateQuoteBody = z.object({
  customerId: Uuid.nullable().optional(),
  channelId: Uuid.optional().describe(
    'Re-evaluates every item for this channel from its frozen calculation.',
  ),
  validUntil: z.iso.date().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  depositBps: z
    .number()
    .int()
    .min(1)
    .max(10_000)
    .nullable()
    .optional()
    .describe('"Sinal" as a share of the total (5000 = 50%); null = no deposit.'),
});
export const NewRevisionBody = z.object({
  note: z
    .string()
    .trim()
    .min(1, 'Escreva uma nota curta sobre a mudança.')
    .max(300)
    .describe('"O que mudou?", e.g. "troca para PLA Basic Dourado, base menor".'),
});
export const DepositPaidBody = z.object({ paid: z.boolean() });
export const DeclineQuoteBody = z.object({
  reason: DeclineReason,
  note: z.string().trim().max(500).nullable().default(null),
});
export const ReopenQuoteBody = z
  .object({
    validUntil: z.iso.date().optional().describe('Defaults to today + validity days.'),
  })
  .nullish();

const Money = z.number().int();
export const QuoteItem = z.object({
  id: Uuid,
  description: z.string(),
  quantity: z.number().int(),
  unitPriceCentavos: Money,
  priceSource: z.enum(['suggested', 'override']),
  suggestedPriceCentavos: Money.nullable(),
  unitCostCentavos: Money,
  netProfitCentavos: Money.describe('Per unit.'),
  marginBps: z.number().int(),
  state: z.enum(['ok', 'below_minimum_margin', 'below_cost']),
  belowMinimumReason: z.string().nullable(),
  perUnit: z.object({ grams: z.number(), printSeconds: z.number().int() }),
  totalPrintSeconds: z.number().int(),
  materials: z.array(z.string()).describe('Slot labels, e.g. "PLA Basic Coral".'),
  calculatedAt: IsoDateTime,
  snapshot: z
    .unknown()
    .describe(
      'Frozen calculation: resolved input, result for every channel, fee versions, engine version.',
    ),
  slicedFile: StoredFile.nullable(),
  thumbnailFileId: Uuid.nullable(),
  variantId: Uuid.nullable().describe('The catalog variant, when the item came from one.'),
});
export type QuoteItem = z.infer<typeof QuoteItem>;

export const QuoteRevisionItem = z.object({
  description: z.string(),
  quantity: z.number().int(),
  unitPriceCentavos: Money,
  materials: z.array(z.string()),
  grams: z.number().describe('Per unit.'),
  printSeconds: z.number().int().describe('Per unit.'),
});
export const QuoteRevision = z
  .object({
    revision: z.number().int(),
    note: z.string().nullable(),
    startedAt: IsoDateTime,
    sentAt: IsoDateTime.nullable(),
    validUntil: z.iso.date(),
    totalCentavos: Money,
    items: z.array(QuoteRevisionItem),
  })
  .meta({ id: 'QuoteRevision' });
export type QuoteRevision = z.infer<typeof QuoteRevision>;

export const QuoteDeposit = z.object({
  bps: z.number().int(),
  centavos: Money.describe('Total × share, rounded to the centavo.'),
  paidAt: IsoDateTime.nullable(),
});
export type QuoteDeposit = z.infer<typeof QuoteDeposit>;

/** "50% · R$ 144,95 · recebido" / "sem sinal" (PDF, convert dialog, order). */
export function depositText(deposit: QuoteDeposit | null): string {
  if (!deposit) return 'sem sinal';
  const pct = (deposit.bps / 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  return `${pct}% · ${formatBRL(deposit.centavos)}${deposit.paidAt ? ' · recebido' : ''}`;
}

export const QuoteEvent = z.object({
  at: IsoDateTime,
  type: z.string(),
  actor: z.string(),
  detail: z.unknown(),
});

export const Quote = z
  .object({
    id: Uuid,
    number: z.string(),
    status: QuoteStatus.describe('A sent quote past its validity reads as `expired`.'),
    customer: z.object({ id: Uuid, name: z.string(), whatsapp: z.string().nullable() }).nullable(),
    channel: z.object({ id: Uuid, name: z.string(), color: z.string() }).nullable(),
    order: z
      .object({ id: Uuid, number: z.string() })
      .nullable()
      .describe('The order it was converted into.'),
    validUntil: z.iso.date(),
    daysUntilExpiry: z.number().int().describe('Negative once expired.'),
    notes: z.string().nullable(),
    declineReason: DeclineReason.nullable(),
    declineNote: z.string().nullable(),
    totals: z.object({
      priceCentavos: Money,
      profitCentavos: Money,
      marginBps: z.number().int(),
      totalPrintSeconds: z.number().int(),
    }),
    leadTimeDays: z
      .number()
      .int()
      .describe('Print hours ÷ print hours per day (queue added later).'),
    revision: z.number().int().describe('Current revision (v1, v2…).'),
    revisionNote: z.string().nullable().describe('"O que mudou?" of the current revision.'),
    revisionAt: IsoDateTime.describe('When the current revision started.'),
    deposit: QuoteDeposit.nullable().describe('"Sinal"; null = no deposit.'),
    items: z.array(QuoteItem),
    attachments: z.array(StoredFile),
    events: z.array(QuoteEvent),
    revisions: z
      .array(QuoteRevision.omit({ items: true }))
      .describe('Earlier revisions, newest first (read-only; items via `GET …/revisions/{n}`).'),
    sentAt: IsoDateTime.nullable(),
    decidedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Quote' });
export type Quote = z.infer<typeof Quote>;

export const QuoteSummary = Quote.omit({
  items: true,
  attachments: true,
  events: true,
  revisions: true,
}).extend({
  itemsSummary: z.string(),
});
export type QuoteSummary = z.infer<typeof QuoteSummary>;
export const QuoteList = z.object({
  data: z.array(QuoteSummary),
  nextCursor: z.string().nullable(),
  counts: z
    .record(QuoteStatus, z.number().int())
    .describe('Per status for the same filters except status.'),
});
export const QuoteQuery = z.object({
  status: QuoteStatus.optional(),
  channelId: Uuid.optional(),
  customerId: Uuid.optional(),
  q: z.string().trim().max(100).optional().describe('Number, customer or item.'),
});

export const QuoteReply = z.object({
  templateId: Uuid.nullable(),
  templateName: z.string().nullable(),
  text: z.string(),
});

/**
 * "Pronto em ~N dias": (hours already in the print queue + this job's hours) ÷ print hours per
 * day, rounded up.
 */
export function leadTimeDays(
  printSeconds: number,
  printHoursPerDay: number,
  queueBacklogSeconds = 0,
): number {
  return Math.max(
    1,
    Math.ceil(
      (printSeconds + Math.max(0, queueBacklogSeconds)) / 3600 / Math.max(1, printHoursPerDay),
    ),
  );
}

export const DEFAULT_REPLY_TEMPLATE =
  'Oi, {cliente}! O orçamento do {produto} fica em {total}.\nPrazo: cerca de {prazo}.\nVálido até {validade}.';

/**
 * Placeholder values for a quote reply ("Copiar resposta"). Shared by the API (saved quotes)
 * and Quick Quote (before saving), so both fill templates the same way.
 */
export function quoteReplyValues(input: {
  customerName: string | null;
  items: { description: string; quantity: number; unitPriceCentavos: number }[];
  leadTimeDays: number;
  validUntil: string;
}): Record<string, string> {
  const total = input.items.reduce((n, i) => n + i.unitPriceCentavos * i.quantity, 0);
  const single = input.items.length === 1 ? input.items[0] : undefined;
  const days = Math.max(1, input.leadTimeDays);
  return {
    cliente: input.customerName?.trim().split(/\s+/)[0] ?? '',
    produto: input.items.map((i) => i.description).join(', '),
    preco_unitario: formatBRL(single ? single.unitPriceCentavos : total),
    quantidade: String(input.items.reduce((n, i) => n + i.quantity, 0)),
    total: formatBRL(total),
    prazo: days === 1 ? '1 dia' : `${days} dias`,
    validade: formatIsoDate(input.validUntil),
  };
}

/**
 * Default reply template: among the quote replies ("…orçamento…"), the marketplace one for
 * channels with a commission, else the Pix/direct one; else the first template.
 */
export function preferredReplyTemplate<T extends { name: string }>(
  templates: T[],
  marketplace: boolean,
): T | undefined {
  const replies = templates.filter((t) => /orçamento/i.test(t.name));
  const list = replies.length ? replies : templates;
  return (
    list.find((t) => (marketplace ? /marketplace|anúncio/i : /pix|direto/i).test(t.name)) ?? list[0]
  );
}
